/**
 * §C.1 · Aceptar una invitación · `/invitacion?token=…`
 *
 * ## Esta dirección la fija el servidor
 *
 * `InviteMemberToClub` manda `${APP_URL}/invitacion?token=…` en el correo. Igual que
 * la ficha pública del club, **es una URL que ya está en la bandeja de alguien** y no
 * se puede renombrar.
 *
 * ## Leer la invitación no la consume, y por eso son dos llamadas
 *
 * `verify` dice a quién invitan, de qué club y con qué rol; `accept` la canjea. El
 * contrato las separa a propósito: la pantalla necesita poder decir «el Dojang Pascual
 * te invita como administrativo» **antes** de gastar un token de un solo uso. Juntas,
 * recargar la página quemaría la invitación.
 *
 * Y por eso aquí `verify` puede ejecutarse dos veces sin consecuencias —React monta
 * dos veces en desarrollo— mientras que `accept` va detrás de un botón que la persona
 * pulsa una vez.
 *
 * ## El nombre se pide, no se da por supuesto
 *
 * El club invita con un correo y a veces con un nombre. Si lo que llega es «Carmen» a
 * secas, la persona puede completarlo aquí: es el único momento en que alguien va a
 * escribir su propio apellido, y hacerlo después exige encontrar una pantalla de
 * cuenta que quizá no busque nunca.
 *
 * ## Sin este fichero, el panel arrancaba un flujo que no podía terminar
 *
 * «Personal» invita, el correo sale, y el enlace no llevaba a ninguna parte: la única
 * forma de aceptar era el `curl` que imprime el seed. Estuvo así hasta el 22-09-2026,
 * y no lo detectó ningún cotejo porque §C es una superficie compartida con las Apps y
 * nadie la medía.
 */
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { api } from '@/shared/api/client';
import { aProblema, type Problema } from '@/shared/api/errores';
import { useSesion } from '@/shared/auth/sesionContexto';
import { Cargando, Error as PanelError } from '@/shared/ui/Estados';

interface Invitacion {
  readonly email: string;
  readonly first_name: string;
  readonly academy_id: string;
  readonly academy_name: string;
  readonly role: string;
  readonly expires_at: string;
}

const ROLES: Readonly<Record<string, string>> = {
  owner: 'dueño',
  admin: 'administrativo',
  coach: 'entrenador',
  assistant: 'ayudante',
  guardian: 'familia',
};

export function Invitacion({ token }: { token: string }) {
  const { entrar } = useSesion();
  const ir = useNavigate();
  const [invitacion, setInvitacion] = useState<Invitacion | null>(null);
  const [problema, setProblema] = useState<Problema | null>(null);
  const [nombre, setNombre] = useState('');
  const [apellidos, setApellidos] = useState('');
  const [aceptando, setAceptando] = useState(false);
  const leida = useRef(false);

  useEffect(() => {
    if (leida.current) return;
    leida.current = true;

    void (async () => {
      const { data, error } = await api.v1.auth.invitations.verify.post({
        token,
      });

      if (error || !data) {
        setProblema(aProblema(error));
        return;
      }

      const i = data as unknown as Invitacion;
      setInvitacion(i);
      setNombre(i.first_name);
    })();
  }, [token]);

  const aceptar = async () => {
    setAceptando(true);
    setProblema(null);

    const { data, error } = await api.v1.auth.invitations.accept.post({
      token,
      first_name: nombre.trim(),
      ...(apellidos.trim() === '' ? {} : { last_name: apellidos.trim() }),
    });

    setAceptando(false);

    if (error || !data) {
      setProblema(aProblema(error));
      return;
    }

    /*
     * El token fuera de la URL antes de entrar: queda en el historial, y de ahí en
     * una captura que alguien pega en un chat. Mismo cuidado que en el canje del
     * enlace mágico.
     */
    const url = new URL(window.location.href);
    url.searchParams.delete('token');
    window.history.replaceState(null, '', url);

    await entrar((data as unknown as { access_token: string }).access_token);

    // Y a la puerta, que abre la aplicación de esta cuenta. Quedarse aquí dejaba
    // en pantalla el formulario ya aceptado, y lo siguiente que tocara la persona
    // —«atrás», una pestaña vieja— decidía a dónde iba.
    await ir({ to: '/', replace: true });
  };

  if (!invitacion && !problema) {
    return <Cargando filas={2} etiqueta="Leyendo la invitación" />;
  }

  if (!invitacion) {
    const caducada = problema?.status === 410 || problema?.status === 404;

    return (
      <main className="acceso">
        <div className="tarjeta">
          <h1>{caducada ? 'Esta invitación ya no vale' : problema?.title}</h1>
          <p>
            {caducada
              ? 'Las invitaciones caducan, y una que ya se aceptó no se puede aceptar dos veces. Pídele al club que te mande otra.'
              : problema?.detail}
          </p>
          <button type="button" onClick={() => window.location.assign('/')}>
            Ir al acceso
          </button>
        </div>
      </main>
    );
  }

  const rol = ROLES[invitacion.role] ?? invitacion.role;

  return (
    <main className="acceso">
      <div className="tarjeta">
        <h1>{invitacion.academy_name} te invita</h1>
        <p className="sub">
          Como <strong>{rol}</strong>, a <b>{invitacion.email}</b>.
        </p>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            void aceptar();
          }}
        >
          <label htmlFor="inv-nombre">Nombre</label>
          <input
            id="inv-nombre"
            required
            maxLength={120}
            autoComplete="given-name"
            value={nombre}
            onChange={(e) => setNombre(e.target.value)}
          />

          <label htmlFor="inv-apellidos">Apellidos</label>
          <input
            id="inv-apellidos"
            maxLength={120}
            autoComplete="family-name"
            value={apellidos}
            onChange={(e) => setApellidos(e.target.value)}
          />

          <button type="submit" disabled={aceptando}>
            {aceptando ? 'Entrando…' : 'Aceptar y entrar'}
          </button>
        </form>

        {/*
          Se dice qué pasa al pulsar. Aceptar abre sesión en el acto —lo hace el
          servidor— y quien no lo espera se sorprende de estar dentro.
        */}
        <p className="sub">
          Al aceptar entras directamente. No hace falta contraseña: podrás
          ponerte una después desde tu cuenta, o entrar siempre con el enlace
          que te mandamos por correo.
        </p>

        {problema && <PanelError problema={problema} />}
      </div>
    </main>
  );
}
